import {
    Component,
    inject,
    input,
    ViewEncapsulation,
    computed,
    output,
    ChangeDetectionStrategy,
    viewChild,
    ElementRef,
    effect,
} from '@angular/core';
import { FiImageSize, FileType } from '@fi-shared/types';
import { MediaBankService } from '../media-bank.service';
import { IMediaBankFile } from '@fi-shared/media-bank';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { MediaViewerService } from '@fi-shared/media-viewer/media-viewer.service';
import { VimeoUnavailablePlaceholderSize } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo-enum';
import { Subject } from 'rxjs';
import { IVideoProgressEvent } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo-player-api';
import { isDefined } from '@fi-app/shared/utils/is-defined';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';

interface IViewSize {
    Height?: number;
    Width?: number;
}

@Component({
    selector: 'fi-media-bank-preview',
    templateUrl: './media-bank-preview.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./media-bank-preview.component.scss'],
    host: { class: 'fi-media-bank-preview' },
    standalone: false,
})
export class MediaBankPreviewComponent {
    public mediaBankFile = input.required<IMediaBankFile>();
    public useDefaultVimeoPreview = input<boolean>(false);
    public videoProgressListener$ = input<Subject<IVideoProgressEvent>>();
    public showInIframeMode = input<boolean>(false);
    public canDownloadMediaBankFile = input<boolean>(false);
    public onAfterRender = output<void>();
    public imageOpened = output<void>();
    public mediaBankFileDownloaded = output<void>();

    private mediaBankService = inject(MediaBankService);
    private mediaStorageService = inject(FiMediaStorageService);
    private mediaViewerService = inject(MediaViewerService);
    private mediaBankHelperService = inject(MediaBankHelperService);
    private sanitizer = inject(DomSanitizer);

    private regularVideoElementRef = viewChild<ElementRef<HTMLVideoElement>>('regularVideoElement');
    private regularVideoElement = computed<HTMLVideoElement | undefined>(() => {
        return this.regularVideoElementRef()?.nativeElement;
    });

    protected isDefined = isDefined;

    private _regularVideoProgressListenerEffect = effect(() => {
        const videoEl = this.regularVideoElement();
        if (!isDefined(videoEl) || !isDefined(this.videoProgressListener$())) {
            return;
        }

        const emitProgress = (progress: { seconds: number; duration: number; percent: number }) => {
            this.videoProgressListener$()?.next(progress);
        };

        const timeupdateHandler = () => {
            const progress = (videoEl.currentTime / videoEl.duration) * 100;
            emitProgress({ seconds: videoEl.currentTime, duration: videoEl.duration, percent: progress });
        };
        const endedHandler = () => {
            emitProgress({ seconds: videoEl.currentTime, duration: videoEl.duration, percent: 100 });
        };
        videoEl.addEventListener('timeupdate', timeupdateHandler);
        videoEl.addEventListener('ended', endedHandler);

        return () => {
            videoEl?.removeEventListener('timeupdate', timeupdateHandler);
            videoEl?.removeEventListener('ended', endedHandler);
        };
    });

    protected readonly FiImageSize = FiImageSize;
    protected readonly FileType = FileType;
    protected readonly VimeoUnavailablePlaceholderSize = VimeoUnavailablePlaceholderSize;

    protected commonVideoViewSize = computed<IViewSize | undefined>(() => {
        const mediaBankFile = this.mediaBankFile();
        if (mediaBankFile.Height && mediaBankFile.Width) {
            let h = mediaBankFile.Height;
            let w = mediaBankFile.Width;
            if (h > 160) {
                const hk = 160 / h;
                h = 160;
                w = w * hk;
            }

            if (w > 500) {
                const wk = 500 / w;
                w = 500;
                h = h * wk;
            }

            return {
                Height: h,
                Width: w,
            };
        }
        return {};
    });

    protected showInIframe = computed<boolean>(() => {
        if (!this.showInIframeMode()) return false;

        const mediaBankFile = this.mediaBankFile();
        return this.mediaStorageService.canShowInIframe(mediaBankFile.FileType, mediaBankFile.Extension);
    });

    protected mediaBankFileIcon = computed<string>(() => {
        return this.mediaBankService.GetMediaBankFileIcon(this.mediaBankFile().FileType);
    });

    protected isVimeoStorage = computed<boolean>(() => {
        return this.mediaStorageService.isVimeoStorage(this.mediaBankFile().StorageType);
    });

    protected getUrlForIframe = computed<SafeResourceUrl>(() => {
        const mediaBankFile = this.mediaBankFile();
        const url = this.mediaStorageService.getMediaFileUrl(mediaBankFile, {
            alwaysUseDownloadFromObjectStorageUrl: true,
            contentDisposition: 'inline',
        });
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    });

    protected getMediaBankFileUrl(size?: FiImageSize): string {
        return this.mediaStorageService.getMediaFileUrl(this.mediaBankFile(), { size: size });
    }

    protected onMediaClick($event: PointerEvent): void {
        $event.preventDefault();
        this.mediaBankHelperService.downloadMediaBankFileRef(this.mediaBankFile(), { openPdfInNewWindow: true });
    }

    protected openMediaViewer(): void {
        const mediaBankFile = this.mediaBankFile();
        if (!mediaBankFile.PhysicalFileName) {
            return;
        }
        this.mediaViewerService.Show(
            this.getMediaBankFileUrl(FiImageSize.Large),
            [mediaBankFile],
            undefined,
            this.videoProgressListener$(),
        );
        if (!this.isVimeoStorage()) {
            this.imageOpened.emit();
        }
    }
}
